import { useForm } from "@inertiajs/react"
import { useEffect } from "react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/text-area"
import { partnerMarketingSettingsPath } from "@/routes"

// IMP-499: what the partner has set, plus the copy they'd get if they set
// nothing — the defaults ride along so the form can show them as placeholders
// rather than describing them in prose.
export interface LandingHero {
  badge: string | null
  heading: string | null
  subheading: string | null
  default_heading: string
  default_subheading: string
}

/**
 * Edits the hero copy on the partner's hub landing page.
 *
 * Every field is optional and blank is meaningful, so nothing here is
 * `required` — clearing a field is how a partner goes back to the template.
 * The placeholders carry that: heading and subheading show the default that
 * would render, the badge shows an example of the relationship it names.
 */
export function LandingHeroSheet({
  hero,
  partnerName,
  open,
  onOpenChange,
}: {
  hero: LandingHero
  partnerName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  // Kept as one object rather than destructured: reset/clearErrors are methods
  // on the form, and pulling them out detaches them from it.
  const form = useForm({
    partner: {
      landing_badge: hero.badge ?? "",
      landing_heading: hero.heading ?? "",
      landing_subheading: hero.subheading ?? "",
    },
  })
  const { data, setData, processing } = form

  // Reopening after a cancel should show what's saved, not what was abandoned.
  useEffect(() => {
    if (!open) {
      form.reset()
      form.clearErrors()
    }
    // The form instance is stable; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const fieldErrors = form.errors as Record<string, string>

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.patch(partnerMarketingSettingsPath(), {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 overflow-y-auto p-0 sm:max-w-lg">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>Landing page settings</SheetTitle>
          <SheetDescription>The opening of your landing page.</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
          <div className="flex flex-1 flex-col gap-6 px-6 py-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="landing-badge">Badge</Label>
              <Input
                id="landing-badge"
                maxLength={80}
                placeholder={`A member benefit from ${partnerName}`}
                value={data.partner.landing_badge}
                onChange={(e) =>
                  setData("partner", {
                    ...data.partner,
                    landing_badge: e.target.value,
                  })
                }
              />
              <p className="text-muted-foreground text-xs">
                The small pill above the heading. Leave blank to show no badge.
              </p>
              <InputError message={fieldErrors?.landing_badge} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="landing-heading">Heading</Label>
              <Textarea
                id="landing-heading"
                rows={2}
                maxLength={120}
                placeholder={hero.default_heading}
                value={data.partner.landing_heading}
                onChange={(e) =>
                  setData("partner", {
                    ...data.partner,
                    landing_heading: e.target.value,
                  })
                }
              />
              <p className="text-muted-foreground text-xs">
                Leave blank to use the default shown.
              </p>
              <InputError message={fieldErrors?.landing_heading} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="landing-subheading">Subheading</Label>
              <Textarea
                id="landing-subheading"
                rows={5}
                maxLength={400}
                placeholder={hero.default_subheading}
                value={data.partner.landing_subheading}
                onChange={(e) =>
                  setData("partner", {
                    ...data.partner,
                    landing_subheading: e.target.value,
                  })
                }
              />
              <p className="text-muted-foreground text-xs">
                Leave blank to use the default shown.
              </p>
              <InputError message={fieldErrors?.landing_subheading} />
            </div>
          </div>

          <SheetFooter className="border-t px-6 py-4">
            <div className="flex gap-2">
              <Button type="submit" size="sm" loading={processing}>
                Save changes
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
